/**
 * Campaign Validation Utilities
 *
 * Three-tier validation system for Campaign Orders:
 * 1. Order-level: Total budget and date range constraints
 * 2. Campaign-level: Budget allocation and date range within order
 * 3. Line item-level: Budget and dates within parent campaign
 */

// ============================================================================
// Date Validation Helpers
// ============================================================================

/**
 * Check if a value is a moment object
 * @param {*} val - Value to check
 * @returns {boolean}
 */
const isMoment = (val) => val && val._isAMomentObject;

/**
 * Normalize a date to midnight UTC for day-level comparison
 * This avoids timezone issues when comparing dates stored with different time components
 * @param {Date} date - Date to normalize
 * @returns {Date} - Date at midnight UTC
 */
const normalizeToUTCDay = (date) => {
  const d = new Date(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

/**
 * Format a date for error messages using UTC components
 * This ensures consistency with how dates are stored
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (M/D/YYYY)
 */
const formatDateUTC = (date) => {
  const d = new Date(date);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
};

/**
 * Format a moment object for error messages
 * @param {Moment} m - Moment object to format
 * @returns {string} - Formatted date string (M/D/YYYY)
 */
const formatMomentDate = (m) => m.format('M/D/YYYY');

// ============================================================================

/**
 * Validates that the sum of campaign budgets does not exceed the order budget
 * Also warns if more than 20% of budget remains unallocated
 *
 * @param {Array} campaigns - Array of campaign objects with budget property
 * @param {number} orderBudget - Total order budget
 * @returns {Object} - { valid: boolean, error: string|null, warning: string|null }
 */
export const validateCampaignBudgets = (campaigns, orderBudget) => {
  if (!campaigns || campaigns.length === 0) {
    return {
      valid: false,
      error: 'At least one campaign is required',
      warning: null,
    };
  }

  const totalCampaignBudget = campaigns.reduce(
    (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
    0
  );

  // Check if campaign budgets exceed order budget
  if (totalCampaignBudget > orderBudget) {
    return {
      valid: false,
      error: `Total campaign budgets ($${totalCampaignBudget.toFixed(2)}) exceed order budget ($${orderBudget.toFixed(2)})`,
      warning: null,
    };
  }

  // Check budget utilization and warn if too much is unallocated
  const utilization = orderBudget > 0 ? (totalCampaignBudget / orderBudget) * 100 : 0;
  const unallocated = orderBudget - totalCampaignBudget;

  let warning = null;
  if (utilization < 80 && unallocated > 0) {
    warning = `${utilization.toFixed(0)}% of budget allocated. $${unallocated.toFixed(2)} remaining unallocated.`;
  }

  return {
    valid: true,
    error: null,
    warning,
    totalAllocated: totalCampaignBudget,
    utilization,
    unallocated,
  };
};

/**
 * Validates that campaign dates fall within order date range
 * Campaign dates are optional and inherit from order if not specified
 *
 * @param {Object} campaign - Campaign object with startDate and endDate (optional)
 * @param {Date|Moment} orderStartDate - Order start date
 * @param {Date|Moment} orderEndDate - Order end date
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateCampaignDates = (campaign, orderStartDate, orderEndDate) => {
  // If campaign has no dates, it inherits from order (always valid)
  if (!campaign.startDate || !campaign.endDate) {
    return {
      valid: true,
      error: null,
      inherited: true,
    };
  }

  // For moment objects, use moment's built-in day comparison which handles timezones correctly
  // Check ALL four dates are moment objects before using moment comparison
  if (
    isMoment(campaign.startDate) &&
    isMoment(campaign.endDate) &&
    isMoment(orderStartDate) &&
    isMoment(orderEndDate)
  ) {
    const campaignStartDate = campaign.startDate;
    const campaignEndDate = campaign.endDate;
    const orderStart = orderStartDate;
    const orderEnd = orderEndDate;

    // Validate campaign start date
    if (campaignStartDate.isBefore(orderStart, 'day')) {
      return {
        valid: false,
        error: `Campaign start date (${formatMomentDate(campaignStartDate)}) is before order start date (${formatMomentDate(orderStart)})`,
        inherited: false,
      };
    }

    // Validate campaign end date
    if (campaignEndDate.isAfter(orderEnd, 'day')) {
      return {
        valid: false,
        error: `Campaign end date (${formatMomentDate(campaignEndDate)}) is after order end date (${formatMomentDate(orderEnd)})`,
        inherited: false,
      };
    }

    // Validate campaign date range
    if (campaignEndDate.isBefore(campaignStartDate, 'day')) {
      return {
        valid: false,
        error: 'Campaign end date must be after start date',
        inherited: false,
      };
    }

    return {
      valid: true,
      error: null,
      inherited: false,
    };
  }

  // Fallback for non-moment dates: Convert to Date objects
  const campaignStart = isMoment(campaign.startDate)
    ? campaign.startDate.toDate()
    : new Date(campaign.startDate);

  const campaignEnd = isMoment(campaign.endDate)
    ? campaign.endDate.toDate()
    : new Date(campaign.endDate);

  const orderStart = isMoment(orderStartDate)
    ? orderStartDate.toDate()
    : new Date(orderStartDate);

  const orderEnd = isMoment(orderEndDate)
    ? orderEndDate.toDate()
    : new Date(orderEndDate);

  const campaignStartDay = normalizeToUTCDay(campaignStart);
  const campaignEndDay = normalizeToUTCDay(campaignEnd);
  const orderStartDay = normalizeToUTCDay(orderStart);
  const orderEndDay = normalizeToUTCDay(orderEnd);

  // Validate campaign start date (compare by calendar day, not exact timestamp)
  if (campaignStartDay < orderStartDay) {
    return {
      valid: false,
      error: `Campaign start date (${formatDateUTC(campaignStart)}) is before order start date (${formatDateUTC(orderStart)})`,
      inherited: false,
    };
  }

  // Validate campaign end date (compare by calendar day, not exact timestamp)
  if (campaignEndDay > orderEndDay) {
    return {
      valid: false,
      error: `Campaign end date (${formatDateUTC(campaignEnd)}) is after order end date (${formatDateUTC(orderEnd)})`,
      inherited: false,
    };
  }

  // Validate campaign date range (compare by calendar day)
  if (campaignEndDay < campaignStartDay) {
    return {
      valid: false,
      error: 'Campaign end date must be after start date',
      inherited: false,
    };
  }

  return {
    valid: true,
    error: null,
    inherited: false,
  };
};

/**
 * Validates that the sum of line item budgets does not exceed campaign budget
 *
 * @param {Array} lineItems - Array of line item objects with budgetTotal property
 * @param {number} campaignBudget - Campaign budget
 * @returns {Object} - { valid: boolean, error: string|null, warning: string|null }
 */
export const validateLineItemBudgets = (lineItems, campaignBudget) => {
  if (!lineItems || lineItems.length === 0) {
    return {
      valid: false,
      error: 'At least one line item is required',
      warning: null,
    };
  }

  const totalLineItemBudget = lineItems.reduce(
    (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
    0
  );

  if (Math.round(totalLineItemBudget * 100) / 100 > Math.round(campaignBudget * 100) / 100) {
    return {
      valid: false,
      error: `Line item budgets ($${totalLineItemBudget.toFixed(2)}) exceed campaign budget ($${campaignBudget.toFixed(2)})`,
      warning: null,
    };
  }

  // Check utilization
  const utilization = campaignBudget > 0 ? (totalLineItemBudget / campaignBudget) * 100 : 0;
  const unallocated = campaignBudget - totalLineItemBudget;

  let warning = null;
  if (utilization < 80 && unallocated > 0) {
    warning = `${utilization.toFixed(0)}% of campaign budget allocated. $${unallocated.toFixed(2)} remaining.`;
  }

  return {
    valid: true,
    error: null,
    warning,
    totalAllocated: totalLineItemBudget,
    utilization,
    unallocated,
  };
};

/**
 * Validates line item dates against campaign or order dates
 *
 * @param {Object} lineItem - Line item with startDate and endDate
 * @param {Date|Moment} campaignStartDate - Campaign start date (or order start if no campaign dates)
 * @param {Date|Moment} campaignEndDate - Campaign end date (or order end if no campaign dates)
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateLineItemDates = (lineItem, campaignStartDate, campaignEndDate) => {
  if (!lineItem.startDate || !lineItem.endDate) {
    return {
      valid: false,
      error: 'Line item start and end dates are required',
    };
  }

  // For moment objects, use moment's built-in day comparison which handles timezones correctly
  // Check ALL four dates are moment objects before using moment comparison
  if (
    isMoment(lineItem.startDate) &&
    isMoment(lineItem.endDate) &&
    isMoment(campaignStartDate) &&
    isMoment(campaignEndDate)
  ) {
    const liStartDate = lineItem.startDate;
    const liEndDate = lineItem.endDate;
    const campStart = campaignStartDate;
    const campEnd = campaignEndDate;

    // Validate line item start date
    if (liStartDate.isBefore(campStart, 'day')) {
      return {
        valid: false,
        error: `Line item start date (${formatMomentDate(liStartDate)}) is before campaign start date (${formatMomentDate(campStart)})`,
      };
    }

    // Validate line item end date
    if (liEndDate.isAfter(campEnd, 'day')) {
      return {
        valid: false,
        error: `Line item end date (${formatMomentDate(liEndDate)}) is after campaign end date (${formatMomentDate(campEnd)})`,
      };
    }

    // Validate line item date range
    if (liEndDate.isBefore(liStartDate, 'day')) {
      return {
        valid: false,
        error: 'Line item end date must be after start date',
      };
    }

    return {
      valid: true,
      error: null,
    };
  }

  // Fallback for non-moment dates: Convert to Date objects
  const lineItemStart = isMoment(lineItem.startDate)
    ? lineItem.startDate.toDate()
    : new Date(lineItem.startDate);

  const lineItemEnd = isMoment(lineItem.endDate)
    ? lineItem.endDate.toDate()
    : new Date(lineItem.endDate);

  const campaignStart = isMoment(campaignStartDate)
    ? campaignStartDate.toDate()
    : new Date(campaignStartDate);

  const campaignEnd = isMoment(campaignEndDate)
    ? campaignEndDate.toDate()
    : new Date(campaignEndDate);

  const lineItemStartDay = normalizeToUTCDay(lineItemStart);
  const lineItemEndDay = normalizeToUTCDay(lineItemEnd);
  const campaignStartDay = normalizeToUTCDay(campaignStart);
  const campaignEndDay = normalizeToUTCDay(campaignEnd);

  // Validate line item dates against campaign dates (compare by calendar day)
  if (lineItemStartDay < campaignStartDay) {
    return {
      valid: false,
      error: `Line item start date (${formatDateUTC(lineItemStart)}) is before campaign start date (${formatDateUTC(campaignStart)})`,
    };
  }

  if (lineItemEndDay > campaignEndDay) {
    return {
      valid: false,
      error: `Line item end date (${formatDateUTC(lineItemEnd)}) is after campaign end date (${formatDateUTC(campaignEnd)})`,
    };
  }

  if (lineItemEndDay < lineItemStartDay) {
    return {
      valid: false,
      error: 'Line item end date must be after start date',
    };
  }

  return {
    valid: true,
    error: null,
  };
};

/**
 * Comprehensive campaign validation that checks all tiers
 * Returns an object with all validation results
 *
 * @param {Object} formValues - Complete form values with order and campaign data
 * @returns {Object} - Comprehensive validation results
 */
export const validateAllCampaigns = (formValues) => {
  const {
    budget: orderBudget,
    startDate: orderStartDate,
    endDate: orderEndDate,
    campaigns = [],
  } = formValues;

  // Order-level validation
  const budgetValidation = validateCampaignBudgets(campaigns, orderBudget);

  // Campaign-level validations
  const campaignValidations = campaigns.map((campaign, index) => {
    const dateValidation = validateCampaignDates(campaign, orderStartDate, orderEndDate);

    // Use campaign dates if available, otherwise use order dates
    const effectiveStartDate = campaign.startDate || orderStartDate;
    const effectiveEndDate = campaign.endDate || orderEndDate;

    const lineItemBudgetValidation = validateLineItemBudgets(
      campaign.lineItems || [],
      campaign.budget || 0
    );

    // Line item date validations
    const lineItemDateValidations = (campaign.lineItems || []).map(lineItem =>
      validateLineItemDates(lineItem, effectiveStartDate, effectiveEndDate)
    );

    return {
      campaignIndex: index,
      campaignName: campaign.name || `Campaign ${index + 1}`,
      dateValidation,
      lineItemBudgetValidation,
      lineItemDateValidations,
      isValid: dateValidation.valid &&
               lineItemBudgetValidation.valid &&
               lineItemDateValidations.every(v => v.valid),
    };
  });

  // Overall validation
  const isValid = budgetValidation.valid &&
                 campaignValidations.every(v => v.isValid);

  return {
    isValid,
    budgetValidation,
    campaignValidations,
    errors: [
      budgetValidation.error,
      ...campaignValidations.flatMap(v => [
        v.dateValidation.error,
        v.lineItemBudgetValidation.error,
        ...v.lineItemDateValidations.map(lv => lv.error),
      ]),
    ].filter(Boolean),
    warnings: [
      budgetValidation.warning,
      ...campaignValidations.map(v => v.lineItemBudgetValidation.warning),
    ].filter(Boolean),
  };
};

/**
 * Gets campaign count constraints
 * @returns {Object} - { min: number, max: number }
 */
export const getCampaignCountConstraints = () => ({
  min: 1,
  max: 10,
});

/**
 * Validates campaign count
 * @param {number} count - Number of campaigns
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateCampaignCount = (count) => {
  const constraints = getCampaignCountConstraints();

  if (count < constraints.min) {
    return {
      valid: false,
      error: `At least ${constraints.min} campaign is required`,
    };
  }

  if (count > constraints.max) {
    return {
      valid: false,
      error: `Maximum ${constraints.max} campaigns allowed per order`,
    };
  }

  return {
    valid: true,
    error: null,
  };
};

/**
 * Detects overlapping date ranges in flight schedules
 *
 * @param {Array} flightSchedules - Array of flight schedule objects with startDate and endDate
 * @returns {Array} - Array of overlap objects { flightIndex1, flightIndex2 } indicating which flights overlap
 */
export const detectFlightOverlaps = (flightSchedules) => {
  if (!flightSchedules || flightSchedules.length <= 1) {
    return [];
  }

  const overlaps = [];
  for (let i = 0; i < flightSchedules.length; i++) {
    for (let j = i + 1; j < flightSchedules.length; j++) {
      const flight1 = flightSchedules[i];
      const flight2 = flightSchedules[j];

      // Skip if either flight is missing dates
      if (!flight1.startDate || !flight1.endDate || !flight2.startDate || !flight2.endDate) {
        continue;
      }

      const start1 = flight1.startDate._isAMomentObject
        ? flight1.startDate.toDate()
        : new Date(flight1.startDate);
      const end1 = flight1.endDate._isAMomentObject
        ? flight1.endDate.toDate()
        : new Date(flight1.endDate);
      const start2 = flight2.startDate._isAMomentObject
        ? flight2.startDate.toDate()
        : new Date(flight2.startDate);
      const end2 = flight2.endDate._isAMomentObject
        ? flight2.endDate.toDate()
        : new Date(flight2.endDate);

      // Check for overlap: ranges overlap if start1 <= end2 AND end1 >= start2
      if (start1 <= end2 && end1 >= start2) {
        overlaps.push({ flightIndex1: i, flightIndex2: j });
      }
    }
  }

  return overlaps;
};
